import { useCallback, useEffect, useRef, useState } from 'react';

import { PreviewPlan } from 'api/video';

/**
 * SYM-6206. The clock behind the Track 1 preview.
 *
 * The Creatomate preview is an iframe with its own player; ours is a plan and a
 * clock. This owns the clock and nothing else: what is drawn at second t is
 * previewTimeline's arithmetic, and what those pictures LOOK like was settled by
 * the server when it drew them.
 *
 * Deliberately the same shape as useCreatomatePreview returns, so the wizard's
 * existing play button, time display and disabled states work for either player
 * rather than the pane growing a second set of controls.
 */
export interface UseTrack1PreviewReturn {
  isReady: boolean;
  isPlaying: boolean;
  currentTime: number;
  play: () => void;
  pause: () => void;
  seek: (seconds: number) => void;
}

export function useTrack1Preview(
  plan: PreviewPlan | null | undefined
): UseTrack1PreviewReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const frame = useRef<number | null>(null);
  /** When the clock was last started, and where it was started from. */
  const startedAt = useRef(0);
  const startedFrom = useRef(0);

  /**
   * Where the clock is, readable without waiting for a render.
   *
   * SYM-6270. The loop below resumes from this. It used to be filled in by an
   * effect that ran AFTER the loop's own effect, so pressing play on a
   * finished preview set the time to 0, the loop still read the end time out
   * of here, saw "already past the end" on its first tick and stopped again:
   * replay did nothing. Whoever moves the clock now writes the new time here
   * in the same breath, so the loop never reads a time that is one render old.
   */
  const currentTimeRef = useRef(0);
  const moveClockTo = useCallback((seconds: number) => {
    currentTimeRef.current = seconds;
    setCurrentTime(seconds);
  }, []);

  const duration = plan?.duration ?? 0;

  const stopFrameLoop = useCallback(() => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current);
      frame.current = null;
    }
  }, []);

  // A new plan is a new video: every edit rebuilds it, and leaving the clock
  // where it was would show a moment that no longer means anything.
  useEffect(() => {
    stopFrameLoop();
    setIsPlaying(false);
    moveClockTo(0);
  }, [plan, stopFrameLoop, moveClockTo]);

  useEffect(() => {
    if (!isPlaying || duration <= 0) {
      stopFrameLoop();
      return undefined;
    }

    startedAt.current = performance.now();
    startedFrom.current = currentTimeRef.current;

    const tick = () => {
      const elapsed = (performance.now() - startedAt.current) / 1000;
      const next = startedFrom.current + elapsed;

      if (next >= duration) {
        // Hold on the last frame rather than snapping back to black: the last
        // slide is the one with the call to action on it.
        moveClockTo(duration);
        setIsPlaying(false);
        return;
      }

      moveClockTo(next);
      frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);

    return stopFrameLoop;
    // currentTime is deliberately absent: it changes every frame, and including
    // it would restart the loop on each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, duration, stopFrameLoop, moveClockTo]);

  const play = useCallback(() => {
    if (duration <= 0) return;
    // Pressing play on a finished video starts it again.
    if (currentTimeRef.current >= duration) moveClockTo(0);
    setIsPlaying(true);
  }, [duration, moveClockTo]);

  const pause = useCallback(() => setIsPlaying(false), []);

  const seek = useCallback(
    (seconds: number) => {
      moveClockTo(Math.min(Math.max(seconds, 0), duration));
    },
    [duration, moveClockTo]
  );

  useEffect(() => stopFrameLoop, [stopFrameLoop]);

  return {
    isReady: !!plan && plan.layers.length > 0,
    isPlaying,
    currentTime,
    play,
    pause,
    seek,
  };
}
